import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import HelpIcon from '@mui/icons-material/Help';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Tooltip,
  useTheme,
  Accordion,
  FormGroup,
  TextField,
  IconButton,
  Typography,
  Autocomplete,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';

import AddEmailers from './AddEmailers';
import useLocales from '../../../hooks/useLocales';
import { Department } from '../../../@types/departments/departments';

// ----------------------------------------------------------------------

type FormEmailReceiversProps = {
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  emailers: string[];
  setEmailers: (emailers: string[]) => void;
  departments: { items: Department[] } | null;
  departmentIds: string[];
  selectedDepartment: Department | null;
  handleDepartmentInputChange: (value: Department | null) => void;
  handleAddDepartment: () => void;
  handleDeleteDepartment: (index: number) => void;
};

const FormEmailReceivers: React.FC<FormEmailReceiversProps> = ({
  isExpanded,
  setIsExpanded,
  emailers,
  setEmailers,
  departments,
  departmentIds,
  selectedDepartment,
  handleDepartmentInputChange,
  handleAddDepartment,
  handleDeleteDepartment,
}) => {
  const { t } = useLocales();
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  return (
    <Accordion sx={{ p: 3 }} expanded={isExpanded}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={[
          {
            px: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          },
          isExpanded
            ? {
                py: 3,
              }
            : {
                py: 2,
              },
        ]}
        onClick={() => setIsExpanded(!isExpanded)}
        id="RQjyHC"
      >
        <Typography variant="h6"> {t('forms.emailReceivers')}</Typography>
        <Box
          sx={[
            {
              px: 2,
            },
            isExpanded
              ? {
                  display: 'block',
                }
              : {
                  display: 'none',
                },
          ]}
        >
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="body1">{t('forms.emailReceiversInfo')}</Typography>
              </Box>
            }
            arrow
            placement="bottom-end"
          >
            <HelpIcon
              sx={[
                isLight
                  ? {
                      color: theme.palette.grey[600],
                    }
                  : {
                      color: theme.palette.grey[500],
                    },
              ]}
            />
          </Tooltip>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ py: 3, pl: 3, pr: 2, pt: 0 }}>
        <Typography variant="body2">{t('forms.departments')}</Typography>

        <FormGroup
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            my: 1,
            mr: 0,
            pr: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Autocomplete
              id="edit-form-email-receivers-select-department"
              value={selectedDepartment}
              onChange={(_, newInputValue) => {
                handleDepartmentInputChange(newInputValue);
              }}
              options={
                departments
                  ? departments.items.filter((d) => {
                      if (departmentIds.indexOf(d.id) === -1) {
                        return d.name;
                      }
                      return undefined;
                    })
                  : []
              }
              getOptionLabel={(option) => option.name}
              fullWidth
              size="small"
              renderInput={(params) => (
                <TextField {...params} label={t('forms.chooseDepartment')} fullWidth />
              )}
            />

            <IconButton
              disabled={selectedDepartment === null}
              onClick={() => handleAddDepartment()}
              id="dzYlHN"
            >
              <AddIcon color={selectedDepartment ? 'primary' : 'disabled'} />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              mt: 1,
              width: '100%',
              maxHeight: '160px',
              overflow: 'auto',
            }}
          >
            {departmentIds.length > 0 &&
              departmentIds.map((d, i) => (
                <Box
                  key={`form-template-${d}`}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography noWrap>
                    {departments ? departments.items.find((dep) => dep.id === d)?.name : ''}
                  </Typography>

                  <IconButton onClick={() => handleDeleteDepartment(i)} id="QT00g0">
                    <ClearIcon fontSize="small" color="error" />
                  </IconButton>
                </Box>
              ))}
          </Box>
        </FormGroup>

        <AddEmailers emailers={emailers} setEmailers={setEmailers} />
      </AccordionDetails>
    </Accordion>
  );
};

export default FormEmailReceivers;
